/**
 * Preferences tab — timezone (drives digest send time) + default
 * account on the Dashboard. Future locale slots (date format, currency)
 * will land here too.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated short list — covers >95% of likely Forge Ledger customers.
// Free-text would be nice but easy to typo into a 400 from the backend.
const TZ_OPTIONS = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Lisbon',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'UTC',
];

const PreferencesTab = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/account/preferences`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load preferences.'));
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/account/preferences`, patch, { withCredentials: true });
      setData(r.data);
      toast.success('Preferences saved.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="text-sm text-[#64748B]">Loading…</div>;

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 max-w-2xl space-y-6" data-testid="preferences-tab">
      <div>
        <h2 className="text-xl font-bold text-[#020617] mb-1">Preferences</h2>
        <p className="text-sm text-[#64748B]">Personal display defaults — date format and currency are locked to UK conventions for now.</p>
      </div>

      <div>
        <Label htmlFor="timezone">Timezone</Label>
        <p className="text-xs text-[#64748B] mb-1.5">Used to send your weekly digest at the right local time.</p>
        <Select value={data.timezone} onValueChange={(v) => save({ timezone: v })}>
          <SelectTrigger className="mt-1" data-testid="pref-timezone-select" disabled={saving}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TZ_OPTIONS.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="default-account">Default Dashboard account</Label>
        <p className="text-xs text-[#64748B] mb-1.5">Which account opens on the Dashboard. Pick &quot;None&quot; to show all.</p>
        <Select
          value={data.default_account_id || '__none__'}
          onValueChange={(v) => save({ default_account_id: v === '__none__' ? '' : v })}
        >
          <SelectTrigger className="mt-1" data-testid="pref-default-account-select" disabled={saving}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None — show all</SelectItem>
            {(data.accounts || []).map((a) => (
              <SelectItem key={a.account_id} value={a.account_id}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="text-xs text-[#94A3B8]">
        Locale: GBP · DD/MM/YYYY (locked)
      </div>
    </div>
  );
};

export default PreferencesTab;
